import { WarningCircleIcon } from '@/components/icons/src/components/icons/warning-circle-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { useDeleteServiceRegistryMutation } from '@/hooks/api/use-delete-service-registry-mutation'
import { T, useTranslate } from '@tolgee/react'

type DeleteServiceRegistryDialogProps = Readonly<{
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceId: string
  serviceName: string
}>

export function DeleteServiceRegistryDialog({
  open,
  onOpenChange,
  serviceId,
  serviceName,
}: DeleteServiceRegistryDialogProps) {
  const { t } = useTranslate()
  const { mutateAsync, isPending } = useDeleteServiceRegistryMutation({ serviceId })

  const handleDeleteButtonClick = async () => {
    try {
      await mutateAsync()
      toast.success(t('serviceRegistry.deleteDialog.toast.success', { serviceName }))
      onOpenChange(false)
    } catch (error) {
      toast.error(t('serviceRegistry.deleteDialog.toast.error'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent disabled={isPending} className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>
            <T keyName='serviceRegistry.deleteDialog.title' />{' '}
            <span className='font-[600]'>{serviceName}</span>
          </DialogTitle>
        </DialogHeader>
        <div className='my-8 flex gap-2'>
          <div>
            <WarningCircleIcon className='shrink-0 text-error-300' />
          </div>
          <div className='flex flex-col gap-4'>
            <p>
              <T keyName='serviceRegistry.deleteDialog.description' />
              <br />
              <span className='font-light'>
                <T keyName='serviceRegistry.deleteDialog.warningText' />
              </span>
            </p>
            <p className='font-light'>
              <T keyName='serviceRegistry.deleteDialog.cautionText' params={{ serviceName }} />
            </p>
          </div>
        </div>
        <DialogFooter className='gap-2'>
          <DialogClose asChild>
            <Button
              variant='mango-outline'
              disabled={isPending}
              title={t('serviceRegistry.deleteDialog.buttons.cancel')}
            >
              <T keyName='serviceRegistry.deleteDialog.buttons.cancel' />
            </Button>
          </DialogClose>
          <Button
            onClick={handleDeleteButtonClick}
            disabled={isPending}
            loading={isPending}
            title={t('serviceRegistry.deleteDialog.buttons.delete')}
          >
            <T keyName='serviceRegistry.deleteDialog.buttons.delete' />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
